/**
 * The upload status bar on the Rig tab (docs/ingest/14-capture-upload-contract.md).
 *
 * The capture PC's uploader sends each capture's photos to the cloud by
 * itself. This bar shows what it last reported ("Uploading 3 of 5, about 4
 * min left") with Pause / Resume. Pause is a request: the bar says
 * "Pausing..." until the uploader confirms "Paused" (it finishes or stops
 * the file in hand first). Resume is always by hand. When the uploader
 * skipped a dataset folder a person must check (a label it cannot read, two
 * folders naming one SN, ...), the bar adds one line naming the folder and
 * why; the other captures keep uploading.
 *
 * It reads through the getUploaderStatus callable, never a listener (the
 * records are server-only). It stays hidden until an uploader has reported on
 * this job; before that it asks every 30 seconds. Then every 3 seconds while
 * something moves (uploading, pausing, resuming, an error), every 10 seconds
 * while idle, paused or not reporting, every 30 seconds in a hidden tab (and
 * at once when the tab shows again). A poll that fails is ignored, the next
 * one waits longer (6, 12, 24, 30 seconds), and the bar keeps what it last
 * showed: nothing here can disturb the rig screen.
 */
import React, { useCallback, useEffect, useState } from "react";
import { rigv2 } from "./rigv2Callables.js";

export const POLL_ACTIVE_MS = 3000;
export const POLL_IDLE_MS = 10000;
export const POLL_QUIET_MS = 30000;
// No good answer for this long: say the bar may be out of date.
export const POLL_OUTDATED_MS = 30000;

const minutesText = (seconds) => {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return "";
  if (seconds < 60) return "under a minute left";
  const m = Math.round(seconds / 60);
  if (m < 90) return `about ${m} min left`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `about ${h} h${rest ? ` ${rest} min` : ""} left`;
};

const agoText = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 90) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
};

// Why the uploader skipped a folder (its reason codes, capture-uploader
// scanner.ts), in the rig screen's words. "Label" is the rig bridge's
// manifest.json, which ties Xangle's folder to a guest's SN.
export const SKIP_REASON_WORDS = {
  manifest_unreadable: "label unreadable",
  manifest_invalid: "label makes no sense",
  duplicate_sn: "same SN as another folder",
  request_mismatch: "label is for another Xangle capture",
  no_photos: "no photos to send",
  set_aside: "set aside after repeated errors",
};
const reasonWords = (code) => SKIP_REASON_WORDS[code] ?? String(code ?? "").replace(/_/g, " ");

/**
 * The line about dataset folders the uploader skipped while it goes on with
 * the others, or "" when there are none. Xangle's folder names and the
 * reasons only (the uploader never sends a guest's name). Pure (tested).
 */
export function skippedFoldersText(skipped) {
  const list = Array.isArray(skipped?.folders) ?
    skipped.folders.filter((f) => f && typeof f.folder === "string" && f.folder) : [];
  const total = Math.max(Number(skipped?.total) || 0, list.length);
  if (total === 0) return "";
  const noun = total === 1 ? "1 folder skipped" : `${total} folders skipped`;
  if (list.length === 0) return `${noun}.`;
  if (total === 1) return `${noun}: ${list[0].folder}, ${reasonWords(list[0].reason)}.`;
  const shown = list.slice(0, 3).map((f) => `${f.folder} (${reasonWords(f.reason)})`);
  const more = total > shown.length ? `, and ${total - shown.length} more` : "";
  return `${noun}: ${shown.join(", ")}${more}.`;
}

/**
 * What the bar says, from the callable's answer. Pure (tested).
 * Returns null when there is nothing to show, else
 * { tone, text, paused, pct, skipped } where paused is the switch the
 * button flips and skipped the line about skipped folders ("" when none).
 */
export function uploadStatusView(answer) {
  const st = answer?.status;
  if (!st) return null;
  const paused = st.pauseRequested === true;
  const pending = Number(st.pending) || 0;
  const done = Number(st.doneCount) || 0;
  const total = Number(st.totalCount) || 0;
  const cur = st.current && typeof st.current === "object" ? st.current : null;
  const pct = cur && cur.bytesTotal > 0 ? Math.max(0, Math.min(100, Math.round((cur.bytesDone / cur.bytesTotal) * 100))) : null;
  if (answer.stale) {
    const at = Number(st.reportedAt);
    const now = Number(answer.now);
    // No report for over 20 s: the uploader is closed, or its computer is off
    // or offline. The screen cannot tell which, so it says both, and what to
    // do: the photos stay on the capture PC until it runs and reaches the Suite.
    const ago = Number.isFinite(at) && Number.isFinite(now) ? ` (last heard from ${agoText(now - at)})` : "";
    return { tone: "muted", text: `Uploader off or offline${ago}. Captures wait on the capture PC: start the uploader there, or check its internet.${paused ? " Paused: press Resume to let it go on when it is back." : ""}`, paused, pct: null, skipped: "" };
  }
  const skipped = skippedFoldersText(st.skippedFolders);
  if (paused && st.state === "paused") {
    return { tone: "amber", text: `Upload paused.${pending ? ` ${pending} waiting.` : ""} Press Resume when the rig is free.`, paused, pct: null, skipped };
  }
  if (paused) return { tone: "amber", text: "Pausing... The uploader stops after the file in hand.", paused, pct, skipped };
  if (st.state === "paused") return { tone: "muted", text: "Resuming...", paused, pct: null, skipped };
  if (st.state === "error") {
    const why = st.lastError ? `: ${st.lastError}` : "";
    return { tone: "red", text: `Upload problem${why}. It keeps trying.`, paused, pct, skipped };
  }
  if (st.state === "idle") {
    return pending > 0 ?
      { tone: "muted", text: `${pending} waiting to upload.`, paused, pct: null, skipped } :
      { tone: "green", text: `Uploads up to date.${done ? ` ${done} in the cloud.` : ""}`, paused, pct: null, skipped };
  }
  const n = total > 0 ? `${Math.min(done + 1, total)} of ${total}` : "";
  const eta = minutesText(st.etaSeconds);
  return { tone: "green", text: `Uploading${n ? ` ${n}` : ""}${eta ? `, ${eta}` : ""}.`, paused, pct, skipped };
}

/**
 * How long to wait before the next poll. Pure (tested). seen: an answer has
 * carried a status; answer: the last good one; failures: polls failed in a
 * row; hidden: the tab is in the background.
 */
export function nextPollDelay({ seen, answer, failures = 0, hidden = false }) {
  if (hidden) return POLL_QUIET_MS;
  if (failures > 0) return Math.min(POLL_ACTIVE_MS * 2 ** failures, POLL_QUIET_MS);
  if (!seen) return POLL_QUIET_MS;
  const st = answer?.status;
  if (!st || answer.stale) return POLL_IDLE_MS;
  const moving = st.state === "running" || st.state === "error" || (st.pauseRequested === true) !== (st.state === "paused");
  return moving ? POLL_ACTIVE_MS : POLL_IDLE_MS;
}

const tabHidden = () => typeof document !== "undefined" && document.hidden === true;

/**
 * The poll loop, outside React so it can be tested with fake timers. Calls
 * load({ jobId }) now and again after each answer, nextPollDelay apart.
 * onAnswer gets each good answer (null when it carries no status); a failed
 * load is ignored. Returns stop().
 */
export function pollUploaderStatus(jobId, { load, onAnswer, schedule = setTimeout, cancel = clearTimeout, hidden = tabHidden }) {
  let stopped = false;
  let timer = null;
  let seen = false;
  let last = null;
  let failures = 0;
  const run = async () => {
    try {
      const r = await load({ jobId });
      if (stopped) return;
      failures = 0;
      if (r && r.status) seen = true;
      last = r && r.status ? r : null;
      onAnswer(last);
    } catch {
      // Not deployed yet, offline, refused: keep what is shown, try again later.
      failures += 1;
    }
    if (!stopped) timer = schedule(run, nextPollDelay({ seen, answer: last, failures, hidden: hidden() }));
  };
  run();
  return () => { stopped = true; if (timer) cancel(timer); };
}

/**
 * Polls getUploaderStatus for this job. { answer, setAnswer, lastOkAt,
 * refresh }: answer is the last good answer with a status, or null.
 */
export function useUploaderStatus(jobId, { load = rigv2.getUploaderStatus } = {}) {
  const [answer, setAnswer] = useState(null);
  const [lastOkAt, setLastOkAt] = useState(0);
  const [kick, setKick] = useState(0);
  useEffect(() => { setAnswer(null); }, [jobId]);
  useEffect(() => {
    if (!jobId) return undefined;
    return pollUploaderStatus(jobId, {
      load,
      onAnswer: (r) => { setLastOkAt(Date.now()); setAnswer(r); },
    });
  }, [jobId, load, kick]);
  const refresh = useCallback(() => setKick((k) => k + 1), []);
  // A tab brought back to the front asks at once instead of after 30 s.
  useEffect(() => {
    if (!jobId || typeof document === "undefined") return undefined;
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [jobId, refresh]);
  return { answer, setAnswer, lastOkAt, refresh };
}

/** The bar itself, from a view (uploadStatusView). Pure: SSR-tested. */
export function UploadStatusStrip({ view, busy = false, error = null, outdated = false, onToggle }) {
  if (!view) return null;
  return (
    <div className={`rigv2-upload ${view.tone}`} role="status">
      <span className="rigv2-upload-dot" aria-hidden="true" />
      <span className="rigv2-upload-text">
        {view.text}
        {view.skipped && <span className="rigv2-upload-skip"> {view.skipped}</span>}
        {outdated && <span className="rigv2-upload-note"> Status not updating; check the connection.</span>}
        {error && <span className="rigv2-upload-err"> {error}</span>}
      </span>
      {view.pct != null && (
        <span className="rigv2-upload-bar" aria-hidden="true"><span style={{ width: `${view.pct}%` }} /></span>
      )}
      <button type="button" className="rigv2-upload-btn" disabled={busy} onClick={onToggle}>
        {view.paused ? "Resume" : "Pause"}
      </button>
    </div>
  );
}

export default function UploadStatusBar({ jobId, load, setPause = rigv2.setUploaderPause }) {
  const { answer, setAnswer, lastOkAt, refresh } = useUploaderStatus(jobId, load ? { load } : undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [, setNow] = useState(0);
  // Re-render now and then so "not updating" appears even when polls hang.
  useEffect(() => {
    if (!answer) return undefined;
    const t = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(t);
  }, [answer]);
  const view = uploadStatusView(answer);
  if (!view) return null;
  const outdated = lastOkAt > 0 && Date.now() - lastOkAt > POLL_OUTDATED_MS;
  const toggle = async () => {
    if (busy) return;
    const next = !view.paused;
    setBusy(true); setError(null);
    try {
      await setPause({ jobId, paused: next });
      setAnswer((a) => (a?.status ? { ...a, status: { ...a.status, pauseRequested: next } } : a));
      refresh();
    } catch (e) {
      setError(`${next ? "Pause" : "Resume"} did not go through: ${e?.message ?? String(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return <UploadStatusStrip view={view} busy={busy} error={error} outdated={outdated} onToggle={toggle} />;
}
